import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getUserActivity, logoutUser } from "../Service/apiService";
import "../assets/css/UserActivityList.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function UserActivityPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  const getResponsiveRows = () => {
    if (window.innerHeight > 900) return 16;   // large screens
    if (window.innerHeight > 750) return 13;   // laptops
    return 9;                                  // small displays
  };
  
  const [rowsPerPage, setRowsPerPage] = useState(getResponsiveRows());

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const data = await getUserActivity();

      const formatted = data?.result?.map((item, index) => ({
        index: index + 1,
        name: item?.userId?.name,
        companyName: item?.userId?.companyName || "-",
        designation: item?.userId?.designation || "-",
        email: item?.userId?.email,
        mobile: item?.userId?.mobile || "-",
        role:
          item?.userId?.role === "admin"
            ? "Admin"
            : item.userId.role === "trial"
            ? "Trial"
            : "Customer",
        action: item?.userId?._id,
      })) ?? [];

      setRows(formatted);
    } catch (error) {
      console.error("Failed to fetch user activity:", error);
      toast.error("Failed to load user activity data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, []);

  useEffect(() => {
    const updateRows = () => setRowsPerPage(getResponsiveRows());
    window.addEventListener("resize", updateRows);
    return () => window.removeEventListener("resize", updateRows);
  }, []);

  const filteredRows = rows.filter((row) =>
    Object.values(row).join(" ").toLowerCase().includes(search.toLowerCase())
  );

  // Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const lastRowIndex = currentPage * rowsPerPage;
  const firstRowIndex = lastRowIndex - rowsPerPage;
  const currentRows = filteredRows.slice(firstRowIndex, lastRowIndex);

  const handleLogout = async (id) => {
    setLoading(true);
    try {
      const res = await logoutUser(id);
      if (res?.response) {
        toast.success("User logged out");
        fetchActivity();
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to logout user");
    } finally {
      setLoading(false);
    }
  };

  // Skeleton table component
  const SkeletonTable = ({ rowCount }) => (
    <tbody>
      {Array.from({ length: rowCount }).map((_, index) => (
        <tr key={index}>
          <td><Skeleton width={30} height={20} /></td>
          <td><Skeleton width={100} height={20} /></td>
          <td><Skeleton width={120} height={20} /></td>
          <td><Skeleton width={100} height={20} /></td>
          <td><Skeleton width={140} height={20} /></td>
          <td><Skeleton width={80} height={20} /></td>
          <td><Skeleton width={60} height={20} /></td>
          <td><Skeleton width={70} height={32} /></td>
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className="user-activity-page">
      <div className="header-row">
        <h2 className="page-title">
          Users Activity List {rows.length > 0 && <span>- {rows.length}</span>}
        </h2>

        <div className="right-controls">
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button
            className="refresh-btn"
            onClick={() => {
              setSearch("");
              fetchActivity();
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="custom-table">
          <thead>
            <tr>
              {[
                "Sr No",
                "Name",
                "Company",
                "Designation",
                "Email",
                "Mobile",
                "Role",
                "Action",
              ].map((h, i) => (
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>

          {loading ? (
            <SkeletonTable rowCount={rowsPerPage} />
          ) : currentRows.length > 0 ? (
            <tbody>
              {currentRows.map((row, i) => (
                <tr key={i}>
                  <td>{firstRowIndex + i + 1}</td>
                  <td>{row.name}</td>
                  <td>{row.companyName}</td>
                  <td>{row.designation}</td>
                  <td>{row.email}</td>
                  <td>{row.mobile}</td>
                  <td>{row.role}</td>
                  <td>
                    <button
                      className="logout-btn"
                      onClick={() => handleLogout(row.action)}
                    >
                      Logout
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan="8">No data found</td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          disabled={currentPage === 1 || loading}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          Prev
        </button>

        <span>
          Page {totalPages === 0 ? 0 : currentPage} / {totalPages || 1}
        </span>

        <button
          disabled={currentPage >= totalPages || loading}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}